import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import QuickLinksCard from '../../../components/Cards/quick-link-card/quick-link-card';
import './quick-link-carousel.scss';

const AUTOPLAY_MS = 10_000;
const SCROLL_END_DEBOUNCE_MS = 150;

// 3-copy clone strategy for seamless infinite loop: render the strip 3× and
// silently rewind scrollLeft by one copy when the user/autoplay drifts into
// the first or last copy. Cloning only kicks in when the content actually
// overflows; if every promo fits, render a single static row.
export default function QuickLinkCarousel({ promos }) {
  const scrollerRef = useRef(null);
  const debounceRef = useRef(null);
  const isHoveredRef = useRef(false);
  const [overflows, setOverflows] = useState(false);

  const slides = overflows ? [...promos, ...promos, ...promos] : promos;

  const stepWidth = () => {
    const item = scrollerRef.current?.firstElementChild?.firstElementChild;
    return item?.getBoundingClientRect().width ?? 0;
  };

  // Measure overflow on mount and on resize so the carousel stays dormant
  // while every card is already visible (e.g., 2 promos at desktop).
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const measure = () => {
      const next = el.scrollWidth > el.clientWidth + 1;
      setOverflows((prev) => (prev === next ? prev : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [promos]);

  // Center the strip in the middle copy whenever we (re-)enter carousel mode.
  useLayoutEffect(() => {
    if (!overflows) return;
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollLeft = el.scrollWidth / 3;
  }, [overflows, promos]);

  // Silent rewind once scrolling settles. Only relevant when we're cloning.
  const handleScroll = useCallback(() => {
    if (!overflows) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const el = scrollerRef.current;
      if (!el) return;
      const cw = el.scrollWidth / 3;
      if (el.scrollLeft < cw * 0.5) el.scrollLeft += cw;
      else if (el.scrollLeft > cw * 1.5) el.scrollLeft -= cw;
    }, SCROLL_END_DEBOUNCE_MS);
  }, [overflows]);

  // Autoplay (paused while hovered, only while overflowing).
  useEffect(() => {
    if (!overflows) return undefined;
    const id = setInterval(() => {
      if (isHoveredRef.current) return;
      advance(1);
    }, AUTOPLAY_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overflows]);

  // Snap to a precise target instead of scrollBy + smooth: scrollBy can land
  // a hair short of the next snap point and the mandatory-snap engine
  // occasionally rubber-bands back to the current point — so the forward
  // arrow appears to do nothing.
  const advance = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = stepWidth();
    if (!step) return;
    const target = Math.round((el.scrollLeft + dir * step) / step) * step;
    el.scrollTo({ left: target, behavior: 'smooth' });
  };

  return (
    <div
      className="quick-links-carousel"
      onMouseEnter={() => (isHoveredRef.current = true)}
      onMouseLeave={() => (isHoveredRef.current = false)}
    >
      {overflows && (
        <button
          type="button"
          aria-label="Previous"
          className="quick-links-carousel__arrow quick-links-carousel__arrow--left"
          onClick={() => advance(-1)}
        >
          ‹
        </button>
      )}
      <div ref={scrollerRef} className="quick-links-carousel__scroller" onScroll={handleScroll}>
        <ul className="quick-links-carousel__track">
          {slides.map((quickLink, i) => (
            <li className="quick-links-carousel__item" key={i}>
              <div className="QuickLinksCard">
                <QuickLinksCard
                  heading={quickLink.Header}
                  image={quickLink.Image}
                  url={quickLink.Link}
                  templateID={quickLink.TemplateID}
                />
              </div>
            </li>
          ))}
        </ul>
      </div>
      {overflows && (
        <button
          type="button"
          aria-label="Next"
          className="quick-links-carousel__arrow quick-links-carousel__arrow--right"
          onClick={() => advance(1)}
        >
          ›
        </button>
      )}
    </div>
  );
}
